import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Close, Help } from '@mui/icons-material';
import NavbarContext from '../../NavbarContext';
import { ToggleButton } from '@mui/material';

class OnlineHelp extends React.Component {

  static propTypes = {
    id: PropTypes.string,
    intl: PropTypes.object,
    children: PropTypes.object,
    navBar: PropTypes.object,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    icon: PropTypes.any,
    title: PropTypes.any,
    shrinkPerDefault: PropTypes.bool,
    updateTimestamp: PropTypes.number,
    skipNavbarButton: PropTypes.bool,
    addNavbarButton: PropTypes.bool,
    skipRememberStatus: PropTypes.bool,
    isAiAssistant: PropTypes.bool,
    className: PropTypes.string,
    isOpenKey: PropTypes.func,
    toggleHelpTimestamp: PropTypes.number
  };

  static contextType = NavbarContext;

  constructor (props) {
    super(props);
    this.state = {
      showHelp: false,
      shrinkedHelp: !!props.shrinkPerDefault
    };
    this.toggleHelp = this.toggleHelp.bind(this);
    this.closeHelp = this.closeHelp.bind(this);
    this.showHelp = this.showHelp.bind(this);
    this.updateGlobalClass = this.updateGlobalClass.bind(this);
  }

  componentDidMount () {
    this.useAnimation(false);
    const shrinkedHelp = document.body.offsetWidth <= 1700 ? true : localStorage.getItem(this.props.id + '.help.visible') ? localStorage.getItem(this.props.id + '.help.visible') !== 'true' : this.state.shrinkedHelp;
    if (this.state.shrinkedHelp !== shrinkedHelp) {
      this.toggleHelp();
    }
    setTimeout(() => { this.setState({ showHelp: true }); }, shrinkedHelp ? 500 : 100);
    setTimeout(() => { this.useAnimation(true); }, 500);
    this.updateGlobalClass();
    this.updateNavbar();
    this.keyupRegistration = document.addEventListener('keydown', (event) => {
      if (this.props.isOpenKey ? this.props.isOpenKey(event) : event?.code === 'F1') {
        if (this.state.shrinkedHelp) { this.showHelp(); } else this.toggleHelp();
        event.preventDefault();
      }
    }, false);
  }

  componentDidUpdate (prevProps) {
    if (prevProps.updateTimestamp !== this.props.updateTimestamp) {
      this.updateNavbar();
    }
    if (prevProps.toggleHelpTimestamp !== this.props.toggleHelpTimestamp) {
      this.toggleHelp();
    }
  }

  componentWillUnmount () {
    this.setState({ showHelp: false });
    document.body.classList.remove('closedHelp');
    document.body.classList.remove('openHelp');
  }

  useAnimation (useAnimation = true) {
    document.body.classList.remove('noAnimation');
    if (!useAnimation) document.body.classList.add('noAnimation');
  }

  updateNavbar () {
    const { navBar } = this.context;
    const { id } = this.props;

    if (!navBar) {
      setTimeout(() => {
        this.updateNavbar();
      }, 200);
    } else {
      if (!this.props.skipNavbarButton) {
        if (this.props.addNavbarButton) {
          navBar.addEntryToHelpContiner(
            id,
            <ToggleButton
              id={id || 'toggleOnlineHelpNavbar'}
              size='small'
              value={this.state.shrinkedHelp}
              selected={!this.state.shrinkedHelp}
              aria-label={this.props.intl.formatMessage({ id: 'help.' + (this.state.shrinkedHelp ? 'expand' : 'collapse') }, { title: this.props.intl.formatMessage({ id: 'help.header' }) })}
              className='borderless'
              onChange={this.toggleHelp}>
              {this.props.icon || <Help />}
            </ToggleButton>
          );
        } else {
          navBar.setHelpContainer(
            <ToggleButton
              id='toggleOnlineHelpNavbar'
              size='small'
              value={this.state.shrinkedHelp}
              selected={!this.state.shrinkedHelp}
              aria-label={this.props.intl.formatMessage({ id: 'help.' + (this.state.shrinkedHelp ? 'expand' : 'collapse') }, { title: this.props.intl.formatMessage({ id: 'help.header' }) })}
              className='borderless'
              onChange={this.toggleHelp}>
              {this.props.icon || <Help />}
            </ToggleButton>
          );
        }
      }
    }
  }

  showHelp () {
    if (this.state.shrinkedHelp) {
      this.toggleHelp();
    }
    document.getElementById('helpContainerScrollPanel')?.focus();
  }

  toggleHelp () {
    this.closeHelp(!this.state.shrinkedHelp);
  }

  closeHelp (closed = true) {
    if (!this.props.skipRememberStatus) {
      localStorage.setItem(this.props.id + '.help.visible', !closed);
    }
    this.setState({ shrinkedHelp: closed }, () => { this.updateGlobalClass(); this.updateNavbar(); if (this.props.onOpen && !closed) this.props.onOpen(); else if (this.props.onClose && closed) this.props.onClose(); });
  }

  updateGlobalClass () {
    const { navBar } = this.context;
    if (this.props.isAiAssistant) {
      document.body.classList.remove('closedAiAssistant');
      document.body.classList.remove('openAiAssistant');
      if (this.state.shrinkedHelp) {
        document.body.classList.add('closedAiAssistant');
      } else {
        document.body.classList.add('openAiAssistant');
      }
    } else {
      const numberOfSelectedHelpButtons = navBar.getNumberOfSelectedHelpButtons();

      document.body.classList.remove('closedHelp');
      document.body.classList.remove('openHelp');
      if (this.state.shrinkedHelp && numberOfSelectedHelpButtons <= 1) {
        document.body.classList.add('closedHelp');
      } else {
        document.body.classList.add('openHelp');
      }
    }
  }

  render () {
    const { id, icon, title, className } = this.props;
    return (
      <div id={id || 'helpContainer'} className={'helpContainer' + (this.state.shrinkedHelp ? ' shrinked' : '') + (className ? ' ' + className : '')}>
        <Paper elevation={3} className={'help description' + (this.state.showHelp ? ' show' : '') + (this.state.shrinkedHelp ? ' shrinked' : '')}>
          <div className="header">
            <div className="startIcon">{icon || <Help />}</div>
            {title || <FormattedMessage id="help.header" />}
            <IconButton
              id='closeOnlineHelp'
              size='small'
              aria-label={this.props.intl.formatMessage({ id: 'help.collapse' }, { title: this.props.intl.formatMessage({ id: 'help.header' }) })}
              onClick={() => this.closeHelp()}>
              <Close />
            </IconButton>
          </div>
          <Typography variant="body2" color="textSecondary" component="div" id="helpContainerScrollPanel" tabIndex={0}>
            {this.props.children}
          </Typography>
        </Paper>
      </div>
    );
  }

}

export default injectIntl(OnlineHelp);
